// Copy for the two founder pages, kept out of the components like content.js.
import { ASSET_PATH } from './content'

export const MARTIN_PORTRAIT = `${ASSET_PATH}/martin-warner-portrait.png`

export const letter = {
  eyebrow: 'A LETTER FROM THE FOUNDER',
  title: 'When Everyone Has AI, What Will Set You Apart?',
  standfirst:
    'Martin Warner on why AI can accelerate your journey, but judgement, resilience and human experience will still shape where you end up.',
  byline: 'Martin Warner, Founder',

  // Rendered in order. `pull` lifts a line out as a pull quote.
  body: [
    'We are living through one of the most exciting moments in entrepreneurship.',
    'AI is changing how we learn, work, build companies and think. It gives founders access to more information, tools and possibilities than ever before.',
    'But information is not insight. The real challenge is knowing what matters, what to trust and what to do next.',
    'That is where judgement, context and experience become even more valuable.',
    'AI can help you learn faster, analyse ideas and explore new possibilities. But great mentors can challenge your assumptions, share their mistakes and help you ask better questions. They can help you move beyond simply collecting information and start making better decisions with confidence.',
    {
      pull: 'I don’t see the future of entrepreneurship education as AI versus humans. I see it as humans supported by AI.',
    },
    'For more than 25 years, I have built businesses, learned from my mistakes and shared those experiences with entrepreneurs. Through Entrepreneur Seminar, we have helped more than 460,000 people take their ideas seriously, make informed decisions and turn ambition into action.',
    'That number represents people with the courage to build something better, for themselves, their families, their teams and their communities.',
    'It is a privilege to have supported so many entrepreneurs, and we never take that responsibility for granted. Every founder has a different idea, different circumstances and different challenges. That is why our focus is not simply on giving you more information. It is on helping you understand what to do with it.',
    'Through practical education, experienced guidance and a community of people on a similar journey, Entrepreneur Seminar is designed to help you think more clearly, avoid costly mistakes and take meaningful steps forward.',
    'We should embrace AI and use it to make education more personalised, accessible and powerful. But we must never lose sight of the person behind the entrepreneur.',
    'Entrepreneurship is a human journey. It requires courage, resilience, adaptability and the willingness to keep learning. It also helps to have people around you who understand the journey, can challenge your thinking and genuinely want to see you succeed.',
    'So use every tool available to you, including AI. But don’t try to navigate this opportunity alone. Surround yourself with people who will challenge and support you, and help you turn information into judgement, and judgement into action.',
    'That is what Entrepreneur Seminar is here to help you do.',
    { pull: 'The next generation of great companies is being built now. Perhaps one of them will be yours.' },
  ],

  closeHeading: 'Where to start.',
  closeBody:
    'Jumpstart is the first step: one month, a challenge matched to your stage, and a straight answer from Martin on what to do next.',
}

export const martinIntro = {
  eyebrow: 'ABOUT MARTIN',
  title: 'One of Britain’s leading entrepreneurs, and a global thought leader on entrepreneurship.',
  lead: 'He is also an educator, AI researcher, inventor, film producer and investor. Today Martin is Founder and CEO of warpSpeed, an app that improves human productivity with a self-learning AI agent and a vertical LLM, and he teaches and mentors directly through Entrepreneur Seminar.',
  caption: 'Martin Warner, Founder',
}

// `note` is optional; it scopes what the claim covers.
export const martinFigures = [
  { stat: '$50M', label: 'Record exit in 17 months', note: 'botObjects, acquired by 3D Systems in January 2015' },
  { stat: '120+', label: 'Patents created' },
  { stat: '12', label: 'Products built and taken to market' },
  { stat: '460,000+', label: 'Entrepreneurs supported', note: 'Across Entrepreneur Seminar since inception' },
]

export const martinVentures = [
  {
    name: 'warpSpeed',
    role: 'Founder and CEO',
    body: 'An app built to improve human productivity, using a self-learning AI agent and a vertical LLM.',
  },
  {
    name: 'Autonomous Flight',
    role: 'Non-Executive Chairman',
    body: 'An electric aviation design and manufacturing firm pioneering eVTOL urban air mobility. Martin was the first British pioneer to enter the eVTOL race.',
  },
  {
    name: 'Flix Premiere',
    role: 'Founder and CEO',
    body: 'The leading independent film streaming service, built to give films that never reach a wide release an audience of their own.',
  },
  {
    name: 'Entrepreneur Seminar',
    role: 'Founder',
    body: 'Practical entrepreneurship education and direct mentoring, built on what actually worked and what did not.',
  },
  // botObjects card removed per Anthony's "Home page Changes" doc (1 Sep 2026);
  // the section now covers current ventures only. Restore by uncommenting.
  // {
  //   name: 'botObjects',
  //   role: 'Co-founder, exited 2015',
  //   body: 'The 3D printing company behind the ProDesk3D, which made the cover of T3 and ranked number one in its Hot 100. Acquired by 3D Systems for $50 million in January 2015, in a record-breaking 17 months.',
  // },
]

export const martinTeaching = {
  heading: 'Why Martin can help.',
  body: 'Most business advice is written by people who have never had to make the decision they are advising you on. Martin has built companies, sold one, and spent 20 years teaching entrepreneurs how to do the same, which means the answer you get is about your situation rather than a case study.',
  points: [
    'Twenty years teaching entrepreneurship directly, not through a curriculum team',
    'Founded Tech Entrepreneurs Week',
    'National bestselling author of The Startup Story: An Entrepreneur\u2019s Journey From Idea to Exit, which debuted on the USA Today Best Seller list',
    'Named in Business Cloud\u2019s top 100 global innovators',
    'Held executive roles at JP Morgan Chase and BearingPoint before founding his own companies',
  ],
}

export const martinInterviews = {
  heading: 'People he has interviewed.',
  body: 'Part of the teaching has always been putting the question to people who have already answered it.',
  names: ['Sir Richard Branson', 'Peter Guber', 'Steve Wozniak', 'Jimmy Wales', 'Carly Fiorina'],
}

export const martinMedia = {
  heading: 'In the media.',
  body: 'Martin appears regularly across UK and international news and press, on entrepreneurship, technology and the businesses being built around AI.',
  outlets: [
    'Sky News',
    'BBC News',
    'BBC World News',
    'Fox Business',
    'Fox',
    'CBS News',
    'TechCrunch',
    'The Guardian',
    'The Times',
    'Financial Times',
    'The Independent',
    'Daily Express',
    'Metro',
    'HuffPost',
    'Variety',
    'Screen',
  ],
}

export const martinClose = {
  heading: 'The shortest way to work with Martin.',
  body: 'Jumpstart is one month: a challenge matched to your stage, your first Foundation module, weekly deep dive clinics, and a written view from Martin on where you actually stand.',
}
